import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

const emptyStaff = { name: '', phone: '', email: '', password: '', is_active: true };

const countryOptions = [
  { code: '+27', flag: '🇿🇦', name: 'South Africa (+27)' },
  { code: '+91', flag: '🇮🇳', name: 'India (+91)' },
  { code: '+1', flag: '🇺🇸', name: 'USA (+1)' },
  { code: '+44', flag: '🇬🇧', name: 'UK (+44)' },
  { code: '+971', flag: '🇦🇪', name: 'UAE (+971)' },
  { code: '+61', flag: '🇦🇺', name: 'Australia (+61)' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore (+65)' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia (+966)' },
];

const resolveFileUrl = (p) => {
  if (!p) return null;
  let clean = String(p).trim();
  if (!clean || ['null', 'undefined', 'none', 'false', '0'].includes(clean.toLowerCase())) return null;
  if (clean.startsWith('http://') || clean.startsWith('https://')) return clean;
  const r2Url = process.env.REACT_APP_R2_PUBLIC_URL || 'https://pub-8403cb60474c4e039483cf5333503261.r2.dev';
  clean = clean.replace(/^\/+/, '').replace(/^uploads\//, '');
  if (!clean || ['null', 'undefined', 'none', 'false', '0'].includes(clean.toLowerCase())) return null;
  return `${r2Url}/${clean}`;
};

function StaffAvatar({ member }) {
  const [imgError, setImgError] = useState(false);
  const imageUrl = resolveFileUrl(member.profile_image);

  if (imageUrl && !imgError) {
    return (
      <img
        src={imageUrl}
        alt={member.name}
        onError={() => setImgError(true)}
        style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }

  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%', background: '#7c3aed',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: 15, fontWeight: 700, flexShrink: 0
    }}>
      {member.name?.[0] || '?'}
    </div>
  );
}

export default function StaffPage() {
  const navigate = useNavigate();
  const { currentBranch, user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sortOption, setSortOption] = useState('a-z');
  const [form, setForm] = useState(emptyStaff);
  const [countryCode, setCountryCode] = useState('+27');
  const [editingMember, setEditingMember] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [selectedBranch, setSelectedBranch] = useState(user?.role === 'center_manager' ? String(currentBranch?.branch_id || '') : '');
  const [selectedClass, setSelectedClass] = useState('');
  const [filterClasses, setFilterClasses] = useState([]);
  const [filterClassesLoading, setFilterClassesLoading] = useState(false);

  // Add/Edit Staff modal classes state
  const [formBranchClasses, setFormBranchClasses] = useState([]);
  const [formSelectedClassIds, setFormSelectedClassIds] = useState([]);
  const [formClassesLoading, setFormClassesLoading] = useState(false);
  
  // Assign Branch state
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedStaffForBranch, setSelectedStaffForBranch] = useState(null);
  const [assignBranchId, setAssignBranchId] = useState('');
  const [isPrimaryBranch, setIsPrimaryBranch] = useState(true);

  // Manager Modal state
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    const activeBranchId = user?.role === 'admin' 
      ? selectedBranch 
      : (currentBranch?.branch_id || selectedBranch);

    if (activeBranchId) {
      loadBranchClassesForFilter(activeBranchId);
    } else {
      setFilterClasses([]);
      setSelectedClass('');
    }
  }, [selectedBranch, currentBranch]);

  async function loadBranchClassesForFilter(branchId) {
    setFilterClassesLoading(true);
    try {
      const res = await api.get(`/branches/${branchId}/classes`);
      const activeClasses = (res.data.data || []).filter(c => c.is_active === 1);
      setFilterClasses(activeClasses);
    } catch (err) {
      setFilterClasses([]);
    } finally {
      setFilterClassesLoading(false);
    }
  }

  useEffect(() => {
    fetchStaff();
  }, [currentBranch, selectedBranch, selectedClass]);

  // Load branch classes whenever form.branch_id or currentBranch changes in Add/Edit modal
  useEffect(() => {
    const targetBranchId = form.branch_id || currentBranch?.branch_id;
    if (isAddModalOpen && targetBranchId) {
      loadBranchClassesForStaffModal(targetBranchId);
    } else {
      setFormBranchClasses([]);
      if (!editingMember) setFormSelectedClassIds([]);
    }
  }, [form.branch_id, currentBranch?.branch_id, isAddModalOpen]);

  async function loadBranchClassesForStaffModal(branchId) {
    setFormClassesLoading(true);
    try {
      const [classesRes, teacherClassesRes] = await Promise.all([
        api.get(`/branches/${branchId}/classes`),
        editingMember ? api.get(`/admin/teachers/${editingMember.id}/classes`) : Promise.resolve({ data: { data: [] } })
      ]);
      const availableClasses = (classesRes.data.data || []).filter(c => c.is_active === 1);
      const teacherAssigned = teacherClassesRes.data.data || [];
      const assignedIds = teacherAssigned.map(c => c.id);

      setFormBranchClasses(availableClasses);
      if (editingMember) {
        setFormSelectedClassIds(assignedIds);
      }
    } catch (err) {
      console.error('Error loading branch classes:', err);
    } finally {
      setFormClassesLoading(false);
    }
  }

  async function loadBranches() {
    try {
      const res = await api.get('/admin/branches');
      setBranches(res.data.data || []);
    } catch (err) {}
  }

  async function fetchStaff() {
    setLoading(true);
    setError('');
    try {
      const params = { role: 'teacher' };
      const activeBranchId = user?.role === 'admin'
        ? selectedBranch
        : (currentBranch?.branch_id || user?.branch_id || selectedBranch);

      if (activeBranchId) params.branch_id = activeBranchId;
      if (selectedClass) params.class_id = selectedClass;

      const response = await api.get('/users', { params });
      setStaff(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load staff members');
    } finally {
      setLoading(false);
    }
  }

  const openAddModal = () => {
    setEditingMember(null);
    setFormSelectedClassIds([]);
    setFormBranchClasses([]);
    setForm(emptyStaff);
    setCountryCode('+27');
    setIsAddModalOpen(true);
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setFormSelectedClassIds([]);
    setFormBranchClasses([]);
    
    let rawPhone = member.phone || '';
    let foundCode = '+27';
    let digitsOnly = rawPhone.replace(/\D/g, '');

    for (const option of countryOptions) {
      const cCodeDigits = option.code.replace('+', '');
      if (rawPhone.startsWith(option.code) || digitsOnly.startsWith(cCodeDigits)) {
        foundCode = option.code;
        if (digitsOnly.startsWith(cCodeDigits)) {
          digitsOnly = digitsOnly.slice(cCodeDigits.length);
        }
        break;
      }
    }

    setCountryCode(foundCode);
    setForm({
      name: member.name || '',
      phone: digitsOnly.slice(0, 10),
      email: member.email || '',
      password: '',
      is_active: Number(member.is_active) === 1,
    });
    setIsAddModalOpen(true);
  };

  const openAssignModal = (member) => {
    setSelectedStaffForBranch(member);
    if (user?.role === 'admin') {
      setAssignBranchId('');
    } else {
      setAssignBranchId(currentBranch?.branch_id ? String(currentBranch.branch_id) : '');
    }
    setIsPrimaryBranch(true);
    setIsAssignModalOpen(true);
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setEditingMember(null);
    setForm(emptyStaff);
    setFormSelectedClassIds([]);
    setFormBranchClasses([]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    let finalPhone = '';
    if (form.phone) {
      const cleanPhone = form.phone.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        setError('Please enter a valid 10-digit mobile phone number');
        return;
      }
      finalPhone = `${countryCode}${cleanPhone}`;
    }

    try {
      const payload = {
        name: form.name.trim(),
        phone: finalPhone || undefined,
        email: form.email ? form.email.trim() : undefined,
        branch_id: form.branch_id || currentBranch?.branch_id,
        class_ids: formSelectedClassIds,
        is_active: form.is_active ? 1 : 0,
      };

      if (editingMember) {
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingMember.id}`, payload);
        setSuccessMessage('Staff/Teacher member updated successfully');
      } else {
        if (form.password) payload.password = form.password;
        await api.post('/users', {
          ...payload,
          role: 'teacher'
        });
        setSuccessMessage('Staff/Teacher member added successfully');
      }

      closeModal();
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save staff member');
    }
  };

  const handleAssignBranch = async (event) => {
    event.preventDefault();
    const targetBranchId = user?.role === 'admin' ? assignBranchId : (currentBranch?.branch_id || assignBranchId);

    if (!targetBranchId) {
      setError('Please select a branch to assign');
      return;
    }
    setError('');
    setSuccessMessage('');
    try {
      await api.post('/admin/branches/assign-teacher', {
        teacher_id: selectedStaffForBranch.id,
        branch_id: parseInt(targetBranchId),
        is_primary: isPrimaryBranch,
      });
      const branchObj = branches.find(b => b.id === parseInt(targetBranchId)) || currentBranch;
      const branchName = branchObj ? branchObj.name : 'Branch';
      setSuccessMessage(`Branch "${branchName}" assigned to "${selectedStaffForBranch.name}" successfully`);
      setIsAssignModalOpen(false);
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to assign branch to staff member');
    }
  };

  const handleUnassignBranch = async (teacherId, branchId, branchName) => {
    if (!window.confirm(`Are you sure you want to remove ${branchName} assignment from this staff member?`)) return;
    setError('');
    setSuccessMessage('');
    try {
      await api.post('/admin/branches/unassign-teacher', {
        teacher_id: teacherId,
        branch_id: branchId,
      });
      setSuccessMessage('Branch assignment removed successfully');
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to remove branch assignment');
    }
  };

  const toggleStatus = async (member) => {
    setError('');
    setSuccessMessage('');
    try {
      const newStatus = Number(member.is_active) === 1 ? 0 : 1;
      await api.put(`/users/${member.id}`, { is_active: newStatus });
      setSuccessMessage(`Account updated successfully`);
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update account status');
    }
  };

  const openManagerModal = (member) => {
    setSelectedStaff(member);
    setIsManagerModalOpen(true);
  };

  const isAlreadyManager = (selectedStaff?.manager_branches || []).some(b => Number(b.branch_id) === Number(currentBranch?.branch_id));

  const saveManagerAccess = async (e) => {
    e.preventDefault();
    if (!selectedStaff || !currentBranch) return;
    setError('');
    setSuccessMessage('');
    try {
      if (isAlreadyManager) {
        const remainingBranchIds = (selectedStaff.manager_branches || [])
          .filter(b => Number(b.branch_id) !== Number(currentBranch.branch_id))
          .map(b => b.branch_id);

        await api.post('/admin/center-managers/set-branches', {
          manager_id: selectedStaff.id,
          branch_ids: remainingBranchIds,
          primary_branch_id: remainingBranchIds[0] || null
        });
        setSuccessMessage(`Center Manager access revoked for ${selectedStaff.name} in ${currentBranch.name}`);
      } else {
        const existingBranchIds = (selectedStaff.manager_branches || []).map(b => b.branch_id);
        const newBranchIds = Array.from(new Set([...existingBranchIds, currentBranch.branch_id]));

        await api.post('/admin/center-managers/set-branches', {
          manager_id: selectedStaff.id,
          branch_ids: newBranchIds,
          primary_branch_id: currentBranch.branch_id
        });
        setSuccessMessage(`${selectedStaff.name} assigned as Center Manager for ${currentBranch.name}`);
      }
      setIsManagerModalOpen(false);
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update manager access');
    }
  };

  if (!currentBranch) {
    return <div style={{ padding: 24 }}>Please select a branch to continue.</div>;
  }

  const sortedStaff = sortItems(staff, sortOption, 'name', 'created_at');

  return (
    <div>
      <PageHeader
        title={`Staff Management — ${currentBranch.name}`}
        subtitle="Create, edit, and manage teacher profiles, branch assignments, and access accounts."
      />
      
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="page-header-row" style={{ alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Active Teachers</h3>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {user?.role === 'admin' && (
            <select
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setSelectedClass('');
              }}
              style={{
                padding: '12px 14px',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                background: '#fff',
                fontSize: 14,
                fontWeight: 500,
                color: '#334155',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                boxSizing: 'border-box'
              }}
            >
              <option value="">All Branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}

          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            disabled={filterClassesLoading}
            style={{
              padding: '12px 14px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              background: '#fff',
              fontSize: 14,
              fontWeight: 500,
              color: '#334155',
              cursor: 'pointer',
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              boxSizing: 'border-box'
            }}
          >
            <option value="">All Classes</option>
            {filterClasses.map(cls => (
              <option key={cls.id} value={cls.id}>
                {cls.name}{cls.section ? ` (${cls.section})` : ''}
              </option>
            ))}
          </select>

          <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
          <button type="button" className="primary-button" onClick={openAddModal}>
            Add Teacher
          </button>
        </div>
      </div>

      {/* Add / Edit Staff (Teacher) Modal */}
      <Modal isOpen={isAddModalOpen} onClose={closeModal} title={editingMember ? 'Edit Staff (Teacher)' : 'Add Staff (Teacher)'}>
        <form onSubmit={handleSubmit}>
          <label>
            Branch {user?.role === 'admin' ? '(Optional)' : ''}
            {user?.role === 'admin' ? (
              <select
                value={form.branch_id || ''}
                onChange={(e) => setForm((p) => ({ ...p, branch_id: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 14, marginTop: 4 }}
              >
                <option value="">Select Branch (Optional)</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            ) : (
              <div style={{
                padding: '10px 14px',
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#334155',
                marginTop: 4
              }}>
                📍 {currentBranch?.name || 'Current Branch'}
              </div>
            )}
          </label>

          {/* Classes Checklist for selected branch */}
          <div style={{ margin: '12px 0 16px', background: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
              Select Classes (Multiple selection allowed)
            </div>
            {formClassesLoading ? (
              <div style={{ fontSize: '12px', color: '#64748b' }}>Loading branch classes...</div>
            ) : formBranchClasses.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                No classes created in this branch yet.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
                {formBranchClasses.map((cls) => {
                  const isChecked = formSelectedClassIds.includes(cls.id);
                  return (
                    <label
                      key={cls.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: isChecked ? 600 : 400,
                        color: isChecked ? '#4338ca' : '#334155',
                        cursor: 'pointer',
                        background: isChecked ? '#e0e7ff' : '#ffffff',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: isChecked ? '1px solid #c7d2fe' : '1px solid #cbd5e1'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormSelectedClassIds(prev => [...prev, cls.id]);
                          } else {
                            setFormSelectedClassIds(prev => prev.filter(id => id !== cls.id));
                          }
                        }}
                      />
                      <span>{cls.name} {cls.section ? `(${cls.section})` : ''}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <label>
            Full Name
            <input placeholder="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
          </label>

          <label>
            Phone Number (10 digits)
            <div style={{ display: 'flex', gap: 6 }}>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                style={{ width: 110, padding: '8px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc', cursor: 'pointer' }}
              >
                {countryOptions.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="10-digit mobile number"
                maxLength={10}
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                style={{ flex: 1 }}
              />
            </div>
            {form.phone && (
              <span style={{ fontSize: 11, color: form.phone.length === 10 ? '#16a34a' : '#dc2626', marginTop: 3, display: 'block', fontWeight: 500 }}>
                {form.phone.length === 10 ? '✓ Valid 10-digit number' : `Entered ${form.phone.length}/10 digits`}
              </span>
            )}
          </label>

          <label>
            Email Address
            <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required />
          </label>

          {user?.role === 'admin' && (
            <label>
              Password {editingMember && <span style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>(Leave blank to keep unchanged)</span>}
              <input
                placeholder={editingMember ? 'New Password (Optional)' : 'Password'}
                type="password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                required={!editingMember}
              />
            </label>
          )}

          {editingMember && (
            <label className="form-checkbox" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#7c3aed' }}
              />
              <span style={{ fontSize: '14px', fontWeight: 600, color: form.is_active ? '#15803d' : '#b91c1c' }}>
                Active Teacher
              </span>
            </label>
          )}

          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={closeModal}>Cancel</button>
            <button type="submit" className="primary-button">{editingMember ? 'Update Staff' : 'Save Teacher'}</button>
          </div>
        </form>
      </Modal>

      {/* Assign Branch Modal */}
      <Modal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} title={`Assign Branch to "${selectedStaffForBranch?.name}"`}>
        <form onSubmit={handleAssignBranch}>
          <label>
            Staff / Teacher Name
            <input
              type="text"
              value={selectedStaffForBranch?.name || ''}
              disabled
              style={{ background: '#f1f5f9', color: '#475569', fontWeight: 600, cursor: 'not-allowed' }}
            />
          </label>

          <label>
            Branch to Assign
            {user?.role === 'admin' ? (
              <select
                value={assignBranchId}
                onChange={(e) => setAssignBranchId(e.target.value)}
                required
                style={{ padding: '8px 12px', width: '100%', borderRadius: 8, border: '1px solid #e5e7eb', marginTop: 4 }}
              >
                <option value="">Choose a branch</option>
                {branches
                  .filter(b => !selectedStaffForBranch?.assigned_branches?.some(ab => ab.branch_id === b.id))
                  .map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))
                }
              </select>
            ) : (
              <div style={{
                padding: '10px 14px',
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#334155',
                marginTop: 4
              }}>
                📍 {currentBranch?.name || 'Current Branch'}
              </div>
            )}
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <input
              type="checkbox"
              checked={isPrimaryBranch}
              onChange={(e) => setIsPrimaryBranch(e.target.checked)}
            />
            Set as Primary Branch
          </label>

          <div className="modal-actions" style={{ marginTop: 24 }}>
            <button type="button" className="secondary-button" onClick={() => setIsAssignModalOpen(false)}>Cancel</button>
            <button type="submit" className="primary-button">Assign Branch</button>
          </div>
        </form>
      </Modal>

      {/* Assign Center Manager Modal */}
      <Modal isOpen={isManagerModalOpen} onClose={() => setIsManagerModalOpen(false)} title={`${isAlreadyManager ? 'Center Manager Access' : 'Assign Center Manager'} — ${selectedStaff?.name}`}>
        <form onSubmit={saveManagerAccess}>
          <p style={{ fontSize: 14, color: '#475569', marginBottom: 14 }}>
            {isAlreadyManager
              ? `This staff member currently has Center Manager access for the active branch:`
              : `Assign Center Manager access to ${selectedStaff?.name} for the active branch:`}
          </p>

          <div style={{
            padding: '14px 18px',
            background: '#f5f3ff',
            border: '1.5px solid #7c3aed',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: 700,
            color: '#6d28d9',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 20
          }}>
            <span>📍 Branch: {currentBranch?.name}</span>
          </div>

          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={() => setIsManagerModalOpen(false)}>Cancel</button>
            <button
              type="submit"
              className="primary-button"
              style={{
                background: isAlreadyManager ? '#dc2626' : '#7c3aed',
                borderColor: isAlreadyManager ? '#dc2626' : '#7c3aed'
              }}
            >
              {isAlreadyManager ? 'Revoke Manager Access' : 'Confirm & Assign'}
            </button>
          </div>
        </form>
      </Modal>

      {loading ? (
        <div>Loading teachers...</div>
      ) : staff.length === 0 ? (
        <div>No teachers registered in this branch.</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Assigned Branches</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedStaff.map((member) => (
                <tr key={member.id}>
                  <td
                    style={{ fontWeight: 600, color: '#2563eb', cursor: 'pointer' }}
                    onClick={() => navigate(`/teachers/${member.id}`)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <StaffAvatar member={member} />
                      <div>
                        <div>{member.name}</div>
                        {member.manager_branches && member.manager_branches.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                            <span style={{ fontSize: 11, background: '#ede9fe', color: '#7c3aed', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                              👔 Manager: {member.manager_branches.map(b => b.branch_name).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{member.phone || '-'}</td>
                  <td>{member.email || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {member.assigned_branches && member.assigned_branches.length > 0 ? (
                        member.assigned_branches.map((b) => {
                          const isDisabled = Number(b.is_active) === 0;
                          return (
                            <span key={b.branch_id} style={{
                              padding: '3px 10px',
                              background: isDisabled ? '#fee2e2' : (b.is_primary ? '#ede9fe' : '#f3f4f6'),
                              color: isDisabled ? '#dc2626' : (b.is_primary ? '#7c3aed' : '#4b5563'),
                              border: isDisabled ? '1px solid #fca5a5' : (b.is_primary ? '1px solid #ddd6fe' : '1px solid #e5e7eb'),
                              borderRadius: 12,
                              fontSize: 12,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              fontWeight: isDisabled ? 700 : 500
                            }}>
                              {b.name} {b.is_primary ? '★' : ''} {isDisabled ? '(Disabled)' : ''}
                              <button
                                type="button"
                                onClick={() => handleUnassignBranch(member.id, b.branch_id, b.name)}
                                style={{
                                  border: 'none',
                                  background: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  padding: '0 2px',
                                  fontSize: 12,
                                  fontWeight: 700
                                }}
                                title="Unassign branch from staff member"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: 13 }}>No branches assigned</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 12,
                      background: Number(member.is_active) === 1 ? '#d1fae5' : '#fee2e2',
                      color: Number(member.is_active) === 1 ? '#065f46' : '#991b1b'
                    }}>
                      {Number(member.is_active) === 1 ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>{member.created_at ? new Date(member.created_at).toLocaleDateString() : '-'}</td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        className="secondary-button"
                        style={{ padding: '4px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => openEditModal(member)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '4px 10px', fontSize: '12px', borderRadius: '6px',
                          border: '1px solid #c7d2fe', backgroundColor: '#e0e7ff', color: '#4338ca',
                          cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => openAssignModal(member)}
                        title={`Assign a branch to ${member.name}`}
                      >
                        🔗 Assign Branch
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          type="button"
                          className="secondary-button"
                          style={{ fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }}
                          onClick={() => openManagerModal(member)}
                        >
                          {member.manager_branches && member.manager_branches.length > 0 ? 'Edit Manager' : 'Assign Manager'}
                        </button>
                      )}
                      <button
                        type="button"
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          borderRadius: '6px',
                          border: Number(member.is_active) === 1 ? '1px solid #fca5a5' : '1px solid #86efac',
                          backgroundColor: Number(member.is_active) === 1 ? '#fef2f2' : '#f0fdf4',
                          color: Number(member.is_active) === 1 ? '#dc2626' : '#16a34a',
                          cursor: 'pointer',
                          fontWeight: 500,
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => toggleStatus(member)}
                      >
                        {Number(member.is_active) === 1 ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
